import React, { useState, useEffect } from 'react';
import { ShoppingCart, X, Trash2, CreditCard, Coins, ExternalLink, Gift, ArrowRight, Plus, CheckCircle, Globe } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../hooks/useCurrency';
import { useI18n } from '../hooks/useI18n';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const CHECKOUT_CURRENCIES = [
  { code: 'eur', symbol: '\u20ac', name: 'EUR' },
  { code: 'usd', symbol: '$', name: 'USD' },
  { code: 'gbp', symbol: '\u00a3', name: 'GBP' },
  { code: 'chf', symbol: 'CHF', name: 'CHF' },
];

export default function CartDrawer() {
  const { items, removeItem, clearCart, total, itemCount, addItem } = useCart();
  const { currency: userCurrency } = useCurrency();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [checkoutCurrency, setCheckoutCurrency] = useState('eur');
  const [payMethod, setPayMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [upsellItems, setUpsellItems] = useState([]);
  const [showUpsell, setShowUpsell] = useState(false);

  const token = localStorage.getItem('user_token');

  useEffect(() => {
    const handler = () => { setOpen(true); loadBalance(); };
    window.addEventListener('open-cart', handler);
    return () => window.removeEventListener('open-cart', handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const orderId = params.get('order_id');
    if (sessionId && orderId) {
      clearCart();
      setResult({ success: true, message: t('cart_payment_success') || 'Payment successful! Your order is being processed.' });
      setOpen(true);
      api.get(`/api/stripe/upsell-suggestions?order_id=${orderId}`)
        .then(d => { if (d.suggestions?.length) { setUpsellItems(d.suggestions); setShowUpsell(true); } })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadBalance = async () => {
    if (!token) return;
    try {
      const data = await api.get('/api/credits/balance');
      setCreditBalance(data.balance || 0);
    } catch { }
  };

  const handleCheckout = async () => {
    if (!token) { window.location.href = '/login'; return; }
    if (items.length === 0) return;
    setProcessing(true);
    setResult(null);

    try {
      if (payMethod === 'credits') {
        const results = [];
        for (const item of items) {
          const res = await api.post('/api/credits/purchase-service', {
            service_key: item.id,
            service_name: item.name,
            amount: parseFloat(item.price) || 0,
            details: { complexity: item.complexity || 'basic' },
          });
          results.push(res);
        }
        const lastBalance = results[results.length - 1]?.new_balance;
        clearCart();
        setResult({ success: true, message: `${t('cart_purchased') || 'Purchased'} ${results.length} ${t('cart_services_with_credits') || 'service(s) with credits!'}`, balance: lastBalance });
        api.get('/api/stripe/upsell-suggestions')
          .then(d => { if (d.suggestions?.length) { setUpsellItems(d.suggestions); setShowUpsell(true); } })
          .catch(() => {});
      } else {
        const currentUrl = window.location.origin + window.location.pathname;
        const data = await api.post('/api/stripe/checkout/cart', {
          items: items.map(i => ({
            id: i.id,
            name: i.name,
            price: parseFloat(i.price) || 0,
          })),
          success_url: currentUrl,
          cancel_url: currentUrl,
          currency: checkoutCurrency,
        });
        if (data.checkout_url) {
          try { await api.post('/api/ecommerce/cart/save', { items }); } catch {}
          window.location.href = data.checkout_url;
          return;
        } else {
          setResult({ success: false, message: t('cart_checkout_no_session') || 'Could not create checkout session' });
        }
      }
    } catch (e) {
      let msg = t('cart_checkout_failed') || 'Checkout failed';
      try { msg = JSON.parse(e.message)?.detail || msg; } catch { msg = e.message || msg; }
      setResult({ success: false, message: msg });
    } finally {
      setProcessing(false);
    }
  };

  const handleUpsellAdd = (item) => {
    addItem({ id: item.id, name: item.title, price: parseFloat(item.price) || 0 });
    setUpsellItems(prev => prev.filter(s => s.id !== item.id));
  };

  if (itemCount === 0 && !open) return null;

  return (
    <>
      {/* Floating cart button */}
      {itemCount > 0 && !open && (
        <button onClick={() => { setOpen(true); loadBalance(); }}
          className="fixed bottom-32 right-6 z-50 w-14 h-14 bg-brand-primary rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform"
          data-testid="cart-floating-btn">
          <ShoppingCart size={22} />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-brand-error rounded-full text-[10px] font-bold flex items-center justify-center text-white"
            data-testid="cart-count-badge">{itemCount}</span>
        </button>
      )}

      {/* Cart drawer */}
      {open && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md bg-bg-secondary border-l border-white/10 h-full flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2" data-testid="cart-title">
                <ShoppingCart size={18} /> {t('cart_title') || 'Cart'} ({itemCount})
              </h3>
              <button onClick={() => { setOpen(false); setShowUpsell(false); }} className="p-1.5 rounded-lg hover:bg-white/5 text-txt-muted" data-testid="cart-close-btn">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {result && (
                <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-brand-success/10 text-brand-success border border-brand-success/20' : 'bg-brand-error/10 text-brand-error border border-brand-error/20'}`}
                  data-testid="cart-result">
                  {result.success && <CheckCircle size={16} className="inline mr-1" />}
                  <p className="inline">{result.message}</p>
                  {result.balance !== undefined && <p className="text-xs mt-1">{t('cart_remaining_balance') || 'Remaining balance:'} {result.balance.toFixed(2)} EUR</p>}
                </div>
              )}

              {/* Upsell Section */}
              {showUpsell && upsellItems.length > 0 && (
                <div className="bg-gradient-to-br from-brand-primary/5 to-brand-teal/5 border border-brand-primary/20 rounded-xl p-4" data-testid="upsell-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift size={16} className="text-brand-primary" />
                    <h4 className="text-sm font-semibold text-txt-primary">{t('cart_complete_setup') || 'Complete your setup'}</h4>
                  </div>
                  <p className="text-xs text-txt-muted mb-3">{t('cart_also_added') || 'Clients who purchased similar services also added:'}</p>
                  <div className="space-y-2">
                    {upsellItems.map((s, i) => (
                      <div key={s.id || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-card border border-white/5" data-testid={`upsell-item-${i}`}>
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm text-txt-primary font-medium truncate">{s.title}</p>
                          <p className="text-[10px] text-txt-muted truncate">{s.description}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {parseFloat(s.price) > 0 && <span className="text-xs font-bold text-brand-primary">{parseFloat(s.price).toFixed(0)}\u20ac</span>}
                          <button onClick={() => handleUpsellAdd(s)}
                            className="flex items-center gap-1 px-2 py-1 rounded-md bg-brand-primary/10 text-brand-primary text-[10px] font-medium hover:bg-brand-primary/20 transition-all border border-brand-primary/20"
                            data-testid={`upsell-add-${i}`}>
                            <Plus size={10} /> {t('cart_add') || 'Add'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {items.length === 0 && !result && (
                <p className="text-sm text-txt-muted text-center py-8" data-testid="cart-empty">{t('cart_empty') || 'Your cart is empty'}</p>
              )}
              {items.map(item => (
                <div key={item.id} className="flex items-start gap-3 p-3 bg-bg-card border border-white/10 rounded-lg" data-testid={`cart-item-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{item.name}</p>
                    <p className="text-xs text-txt-muted">{item.complexity || (t('cart_standard') || 'Standard')}</p>
                  </div>
                  <p className="text-sm font-semibold text-brand-primary flex-shrink-0">{parseFloat(item.price).toFixed(0)} EUR</p>
                  <button onClick={() => removeItem(item.id)} className="p-1 rounded hover:bg-brand-error/10 text-txt-muted hover:text-brand-error" data-testid={`cart-remove-${item.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-4 border-t border-white/5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-txt-secondary">{t('cart_total') || 'Total'}</span>
                  <span className="font-bold text-white" data-testid="cart-total">{total.toFixed(2)} EUR</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPayMethod('stripe')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border transition-all ${payMethod === 'stripe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-white/10 text-txt-muted hover:text-white'}`}
                    data-testid="cart-pay-stripe">
                    <CreditCard size={12} /> {t('cart_stripe_checkout') || 'Stripe Checkout'}
                  </button>
                  <button onClick={() => setPayMethod('credits')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium border transition-all ${payMethod === 'credits' ? 'border-brand-teal bg-brand-teal/10 text-brand-teal' : 'border-white/10 text-txt-muted hover:text-white'}`}
                    data-testid="cart-pay-credits">
                    <Coins size={12} /> {t('cart_credits') || 'Credits'} {creditBalance !== null ? `(${creditBalance.toFixed(0)})` : ''}
                  </button>
                </div>
                {payMethod === 'stripe' && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-txt-muted flex items-center gap-1"><ExternalLink size={10} /> {t('cart_secure_stripe') || 'Secure Stripe checkout'}</p>
                    <div className="flex items-center gap-1" data-testid="checkout-currency-selector">
                      <Globe size={12} className="text-txt-muted" />
                      {CHECKOUT_CURRENCIES.map(c => (
                        <button key={c.code} onClick={() => setCheckoutCurrency(c.code)}
                          className={`px-2 py-1 text-[10px] rounded-md border transition-all ${checkoutCurrency === c.code ? 'border-brand-primary bg-brand-primary/10 text-brand-primary font-medium' : 'border-white/10 text-txt-muted hover:text-white'}`}
                          data-testid={`currency-${c.code}`}>{c.symbol} {c.name}</button>
                      ))}
                    </div>
                  </div>
                )}
                {payMethod === 'credits' && creditBalance !== null && creditBalance < total && (
                  <p className="text-xs text-brand-warning" data-testid="cart-insufficient">{t('cart_insufficient') || 'Insufficient credits. Top up first or choose card payment.'}</p>
                )}
                <button onClick={handleCheckout} disabled={processing || (payMethod === 'credits' && creditBalance !== null && creditBalance < total)}
                  className="w-full py-3 bg-brand-primary text-white rounded-lg font-medium text-sm hover:bg-brand-primary/80 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  data-testid="cart-checkout-btn">
                  {processing ? (t('cart_processing') || 'Processing...') : <>{payMethod === 'stripe' ? (t('cart_pay_card') || 'Pay with Card') : (t('cart_pay_credits_btn') || 'Pay with Credits')} <ArrowRight size={14} /></>}
                </button>
                <button onClick={clearCart} className="w-full text-xs text-txt-muted hover:text-brand-error text-center" data-testid="cart-clear">
                  {t('cart_clear') || 'Clear cart'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
